"use client";

import { Download, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Taste() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: subRef,
    isVisible: subVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: contactRef,
    isVisible: contactVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4509" id="taste-of-the-wild-cta" className="dark bg-primary text-white relative overflow-hidden py-32 md:py-56 px-6 md:px-12">
      {}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.03]">
        <svg className="w-full h-48" viewBox="0 0 1600 200" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <g fill="white">
            {Array.from({
            length: 120
          }).map((_, i) => {
            const heights = [12, 28, 44, 68, 92, 120, 88, 56, 36, 22, 48, 76, 104, 132, 98, 64, 40, 24, 16, 32];
            const h = heights[i % heights.length];
            return <rect key={i} data-index={i} x={i * 14} y={100 - h / 2} width={3} height={h} rx={1.5} />;
          })}
          </g>
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {}
          <div className="lg:col-start-2 lg:col-span-8">
            {}
            <div ref={eyebrowRef} className={`flex items-center gap-4 transition-all duration-700 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="h-px w-12 bg-white/40" aria-hidden="true" />
              <span className="font-mono text-xs uppercase tracking-widest text-white/70">
                Wild-Caught. Expertly Cut. Delivered with Care.
              </span>
            </div>

            {}
            <h2 ref={headlineRef} className={`font-default mt-8 text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-white transition-all duration-1000 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <span className="block font-medium">Experience the</span>
              <span className="block italic font-light mt-2">Taste of the Wild.</span>
            </h2>

            {}
            <p ref={subRef} className={`font-default mt-10 text-xl md:text-2xl text-white/80 max-w-2xl leading-relaxed transition-all duration-1000 delay-150 ease-out ${subVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              From the deck of The Miss Yvonne to your kitchen table, enjoy premium, sustainably harvested seafood delivered directly to your door.
            </p>

            {}
            <div ref={ctaRef} className={`mt-12 flex flex-col sm:flex-row gap-4 sm:gap-6 transition-all duration-1000 delay-300 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Button asChild size="lg" className="px-10 h-14 text-base font-default tracking-wide shadow-lg transition-colors duration-300 bg-secondary text-white hover:bg-white hover:text-primary">
                <Link to="/ShopPage">Shop Seafood Now</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="px-8 h-14 text-base font-default tracking-wide bg-transparent border-2 border-white/60 text-white hover:bg-white/10 hover:text-white transition-colors duration-300">
                <Link to="/SustainabilityPage">
                  <Download className="size-4" />
                  Our Sustainability Promise
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={contactRef} className={`lg:col-start-10 lg:col-span-3 lg:pl-6 lg:border-l lg:border-white/20 transition-all duration-1000 delay-500 ease-out ${contactVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`}>
            <div className="space-y-6">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 block mb-2">
                  Inquiries
                </span>
                <Link to="tel:+12285550196" className="font-default text-2xl md:text-3xl text-white hover:text-white/70 transition-colors flex items-center gap-2 no-underline">
                  <Phone className="size-4 opacity-60" aria-hidden="true" />
                  (228) 555-0196
                </Link>
              </div>

              <div className="h-px w-8 bg-white/30" aria-hidden="true" />

              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 block mb-2">
                  Correspondence
                </span>
                <Link to="mailto:hello@themissyvonne.com" className="font-default italic text-xl md:text-2xl text-white hover:text-white/70 transition-colors break-words no-underline">
                  hello@themissyvonne.com
                </Link>
              </div>

              <div className="h-px w-8 bg-white/30" aria-hidden="true" />

              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 block mb-2">
                  Home Port
                </span>
                <address className="not-italic font-sans text-sm text-white/80 leading-relaxed space-y-1">
                  <div>Ocean Springs,</div>
                  <div>Mississippi</div>
                  <div>Est. 1965</div>
                </address>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}